import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { getNavigationCategories } from '../../lib/api';
import { LOGO_SRC } from '../../lib/branding';
import { HeaderProductSearch } from './HeaderProductSearch';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [navCategories, setNavCategories] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpandedCategory, setMobileExpandedCategory] = useState(null);
  const dropdownTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    getNavigationCategories()
      .then(res => setNavCategories(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileSearchOpen(false);
    setMobileExpandedCategory(null);
    setActiveDropdown(null);
  }, [location]);

  const handleMouseEnter = (categoryId) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setActiveDropdown(categoryId);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 150);
  };

  const toggleMobileCategory = (categoryId) => {
    setMobileExpandedCategory(
      mobileExpandedCategory === categoryId ? null : categoryId
    );
  };

  const openMobileSearch = () => {
    setIsMobileMenuOpen(false);
    setIsMobileSearchOpen(true);
  };

  const closeMobileSearch = () => setIsMobileSearchOpen(false);

  // Calculate columns for brand dropdown (8 brands per column, max 40 brands shown)
  const getBrandColumns = (brands) => {
    if (!brands || brands.length === 0) return [];
    const maxBrands = 40;
    const displayBrands = brands.slice(0, maxBrands);
    const itemsPerColumn = 8;
    const columnCount = Math.ceil(displayBrands.length / itemsPerColumn);
    const columns = [];
    for (let i = 0; i < columnCount; i++) {
      columns.push(displayBrands.slice(i * itemsPerColumn, (i + 1) * itemsPerColumn));
    }
    return columns;
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0A0A0A] shadow-lg' : 'bg-[#0A0A0A]/95 backdrop-blur-sm'
      }`}
    >
      {/* Line 1: Logo (left) | Search, Contact & Download Catalog (right) */}
      <div className="border-b border-white/10">
        <div className="container-custom">
          <div className="flex items-center justify-between gap-4 h-14 md:h-16">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
              <img
                src={LOGO_SRC}
                alt="Silver Perfumes"
                className="h-10 md:h-12 w-auto logo-blend"
              />
            </Link>

            {/* Right: Search, Products, Contact & Download Catalog (Desktop) */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-6 flex-1 justify-end min-w-0">
              <HeaderProductSearch variant="desktop" />
              <Link
                to="/products"
                className={`text-sm uppercase tracking-[0.15em] font-medium transition-colors text-white/80 hover:text-[#C0C4CC] flex-shrink-0 ${
                  location.pathname === '/products' ? 'text-[#C0C4CC]' : ''
                }`}
                data-testid="nav-products"
              >
                Products
              </Link>
              <Link
                to="/contact"
                className={`text-sm uppercase tracking-[0.15em] font-medium transition-colors text-white/80 hover:text-[#C0C4CC] flex-shrink-0 ${
                  location.pathname === '/contact' ? 'text-[#C0C4CC]' : ''
                }`}
                data-testid="nav-contact"
              >
                Contact
              </Link>
              <Link
                to="/download-catalog"
                className="px-5 py-2 bg-[#C0C4CC] text-[#0A0A0A] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#E8EAEF] transition-all duration-300 flex-shrink-0"
                data-testid="download-catalog-btn"
              >
                Download Catalog
              </Link>
            </div>

            {/* Mobile: Search + Menu */}
            <div className="lg:hidden flex items-center gap-1">
              <button
                type="button"
                className="p-2"
                onClick={openMobileSearch}
                aria-label="Search products"
                data-testid="mobile-search-toggle"
              >
                <Search className="h-5 w-5 text-white" />
              </button>
              <button
                className="p-2"
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }}
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 text-white" />
                ) : (
                  <Menu className="h-6 w-6 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile search bar */}
      {isMobileSearchOpen && (
        <div
          className="lg:hidden border-b border-white/10 px-4 py-3 bg-[#0A0A0A]"
          data-testid="mobile-search-bar"
        >
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <HeaderProductSearch
                variant="mobile-inline"
                autoFocus
                onNavigate={closeMobileSearch}
              />
            </div>
            <button
              type="button"
              className="p-2 text-white/70 hover:text-white flex-shrink-0"
              onClick={closeMobileSearch}
              aria-label="Close search"
              data-testid="mobile-search-close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Line 2: Product Categories (centered) */}
      <div className="hidden lg:block">
        <div className="container-custom">
          <nav className="flex items-center justify-center gap-2 xl:gap-4 h-12" data-testid="desktop-nav">
            {navCategories.map((category) => (
              <div
                key={category.id}
                className="relative"
                onMouseEnter={() => handleMouseEnter(category.id)}
                onMouseLeave={handleMouseLeave}
              >
                <Link
                  to={`/category/${category.slug}`}
                  className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 text-xs xl:text-sm uppercase tracking-[0.12em] font-medium transition-all duration-200 text-white/90 hover:text-[#C0C4CC] ${
                    location.pathname.includes(`/category/${category.slug}`) ? 'text-[#C0C4CC]' : ''
                  } ${activeDropdown === category.id ? 'text-[#C0C4CC]' : ''}`}
                  data-testid={`nav-category-${category.slug}`}
                >
                  {category.name}
                  {category.brands && category.brands.length > 0 && (
                    <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${
                      activeDropdown === category.id ? 'rotate-180' : ''
                    }`} />
                  )}
                </Link>

                {/* Multi-Column Brand Dropdown */}
                {category.brands && category.brands.length > 0 && (
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-0 bg-white shadow-2xl border-t-2 border-[#C0C4CC] transition-all duration-200 ${
                      activeDropdown === category.id
                        ? 'opacity-100 visible translate-y-0'
                        : 'opacity-0 invisible -translate-y-2 pointer-events-none'
                    }`}
                    style={{ minWidth: `${Math.min(Math.ceil(category.brands.length / 8), 4) * 160 + 40}px` }}
                    data-testid={`dropdown-${category.slug}`}
                  >
                    {/* Category Header */}
                    <div className="px-5 py-3 bg-[#F4F4F5] border-b border-gray-100">
                      <Link
                        to={`/category/${category.slug}`}
                        className="text-sm font-semibold text-[#0A0A0A] hover:text-[#C0C4CC] transition-colors uppercase tracking-wider"
                        data-testid={`view-all-${category.slug}`}
                      >
                        View All {category.name} →
                      </Link>
                    </div>

                    {/* Brand Columns */}
                    <div className="p-4 flex gap-6">
                      {getBrandColumns(category.brands).map((column, colIndex) => (
                        <div key={colIndex} className="flex-1 min-w-[140px]">
                          {column.map((brand) => (
                            <Link
                              key={brand.id}
                              to={`/category/${category.slug}/brand/${brand.slug}`}
                              className="block py-2 text-sm text-gray-600 hover:text-[#C0C4CC] hover:pl-1 transition-all duration-150"
                              data-testid={`brand-link-${brand.slug}`}
                            >
                              {brand.name}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>

                    {/* See More button for categories with more than 40 brands */}
                    {category.brands.length > 40 && (
                      <div className="px-5 py-3 bg-[#F4F4F5] border-t border-gray-100">
                        <Link
                          to={`/category/${category.slug}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-[#C0C4CC] hover:text-[#0A0A0A] transition-colors"
                          data-testid={`see-more-${category.slug}`}
                        >
                          See More ({category.brands.length - 40} more brands) →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed top-14 left-0 right-0 h-[calc(100vh-3.5rem)] bg-white z-[10000] transform transition-transform duration-300 overflow-y-auto ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 gap-2">
          {/* Search inside mobile menu */}
          <div className="pb-4 mb-2 border-b border-gray-100">
            <HeaderProductSearch
              variant="mobile-panel"
              onNavigate={() => setIsMobileMenuOpen(false)}
            />
          </div>

          {/* Categories with expandable brands */}
          {navCategories.map((category) => (
            <div key={category.id} className="border-b border-gray-100 pb-2">
              <div className="flex items-center justify-between">
                <Link
                  to={`/category/${category.slug}`}
                  className={`text-lg font-medium py-2 flex-1 ${
                    location.pathname.includes(`/category/${category.slug}`)
                      ? 'text-[#C0C4CC]'
                      : 'text-[#0A0A0A]'
                  }`}
                  data-testid={`mobile-category-${category.slug}`}
                >
                  {category.name}
                </Link>
                {category.brands && category.brands.length > 0 && (
                  <button
                    onClick={() => toggleMobileCategory(category.id)}
                    className="p-2 text-gray-400"
                    data-testid={`mobile-expand-${category.slug}`}
                  >
                    <ChevronDown className={`h-5 w-5 transition-transform ${
                      mobileExpandedCategory === category.id ? 'rotate-180' : ''
                    }`} />
                  </button>
                )}
              </div>

              {/* Mobile brands dropdown */}
              {category.brands && category.brands.length > 0 && mobileExpandedCategory === category.id && (
                <div className="pl-4 py-2 space-y-1 bg-gray-50 -mx-6 px-10">
                  {category.brands.map((brand) => (
                    <Link
                      key={brand.id}
                      to={`/category/${category.slug}/brand/${brand.slug}`}
                      className="block text-sm text-gray-600 py-1.5 hover:text-[#C0C4CC]"
                      data-testid={`mobile-brand-${brand.slug}`}
                    >
                      {brand.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Products */}
          <Link
            to="/products"
            className={`text-lg font-medium py-3 ${
              location.pathname === '/products' ? 'text-[#C0C4CC]' : 'text-[#0A0A0A]'
            }`}
            data-testid="mobile-products"
          >
            Products
          </Link>

          {/* Contact */}
          <Link
            to="/contact"
            className={`text-lg font-medium py-3 ${
              location.pathname === '/contact' ? 'text-[#C0C4CC]' : 'text-[#0A0A0A]'
            }`}
            data-testid="mobile-contact"
          >
            Contact
          </Link>

          {/* Download Catalog CTA */}
          <div className="pt-4 border-t border-gray-100 mt-2">
            <Link
              to="/download-catalog"
              className="btn-primary block text-center"
              data-testid="mobile-download-catalog"
            >
              Download Catalog
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
